import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Alert,
  Anchor,
  Avatar,
  Badge,
  Button,
  Card,
  Collapse,
  ColorInput,
  Group,
  Skeleton,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core"
import { modals } from "@mantine/modals"
import { IconArrowMerge, IconPlayerPlay, IconPlayerStop, IconTrash } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { resolvePlaybackSrc } from "../../../lib/audio-format"
import { SPEAKER_COLORS } from "../../../lib/speaker-colors"
import { summariseSpeakerEvidence } from "../../../lib/speaker-evidence"
import { speakerInitials } from "../../../lib/speaker-format"
import { SpeakerRosterPicker } from "../../SpeakerRosterPicker"
import type { TSpeakerRosterChoice } from "../../SpeakerRosterPicker/interfaces"
import type { IViewProps, TSpeakerEditIntent } from "./interfaces"

/**
 * Dumb view for SpeakerCard. The smart wrapper at ../index.tsx owns the
 * renameSpeaker / patchSpeakerProfile / fetchSpeakerLines calls and the
 * accompanying notifications; this component renders the expand/collapse
 * card, holds presentation-only state (the form inputs, the voice-sample
 * <audio> element, expanded toggle), and emits intent via callback props.
 */
export function SpeakerCardView({
  speakerDisplayName,
  mergeOptions,
  quotes,
  fallbackLines,
  loadingLines,
  isUnknown,
  profile,
  autoColor,
  autoTitle,
  defaultOpen = false,
  knownVoiceId = null,
  presence,
  showPresence = false,
  speakerSegments,
  audioUrl,
  saving,
  savingProfile,
  discarding,
  error,
  onLoadLines,
  onSubmit,
  onSaveProfile,
  onDiscard,
  suggestion,
  onConfirmSuggestion,
  onRejectSuggestion,
  suggestionBusy = false,
  rosterPeople = [],
  rosterLoading = false,
  onRosterQueryChange,
}: IViewProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [expanded, setExpanded] = useState(defaultOpen)
  const [newName, setNewName] = useState("")
  const [displayNameDraft, setDisplayNameDraft] = useState(speakerDisplayName)
  const [mergeTarget, setMergeTarget] = useState("")
  const [pickerOpen, setPickerOpen] = useState(false)

  // Human-readable evidence behind the AI suggestion ("voice match", …).
  const suggestionEvidence = useMemo(
    () => (suggestion ? summariseSpeakerEvidence(suggestion.sources, i18n) : ""),
    [suggestion, i18n],
  )

  // "Use this name" pre-fills the rename draft so the user can tweak before
  // committing, instead of a one-shot confirm.
  const handleUseSuggestion = useCallback((): void => {
    if (!suggestion) return
    setMergeTarget("")
    setNewName(suggestion.name)
    setDisplayNameDraft(suggestion.name)
  }, [suggestion])

  useEffect(() => {
    setDisplayNameDraft(speakerDisplayName)
  }, [speakerDisplayName])

  // Profile editing state — pre-fill from saved profile, then auto-detected fallbacks.
  const [editTitle, setEditTitle] = useState(profile?.title ?? autoTitle ?? "")
  const [editColor, setEditColor] = useState(profile?.color ?? autoColor ?? "")

  // Voice sample playback (pure browser state — kept here, not in the smart wrapper).
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playingIndex, setPlayingIndex] = useState<number | null>(null)

  // Pick up to 3 representative segments: at least 5 words, spread across the recording.
  const sampleSegments = useMemo(() => {
    if (!speakerSegments?.length) return []
    const good = speakerSegments.filter((s) => s.isFinal && s.text.trim().split(/\s+/).length >= 5)
    if (good.length <= 3) return good
    const step = Math.floor(good.length / 3)
    return [good[0], good[step], good[good.length - 1]]
  }, [speakerSegments])

  // When opened directly (modal) with no pre-loaded quotes, ask the smart
  // wrapper to fetch fallback lines on mount.
  useEffect(() => {
    if (defaultOpen && quotes.length === 0 && fallbackLines.length === 0 && !loadingLines) {
      onLoadLines()
    }
  }, [defaultOpen, quotes.length, fallbackLines.length, loadingLines, onLoadLines])

  const handleExpand = useCallback((): void => {
    if (!expanded && quotes.length === 0 && fallbackLines.length === 0 && !loadingLines) {
      onLoadLines()
    }
    setExpanded(!expanded)
  }, [expanded, quotes.length, fallbackLines.length, loadingLines, onLoadLines])

  // Run the rename/merge and only clear the draft + collapse the card once the
  // request resolves without throwing. A failed rename used to wipe the typed
  // name and collapse the card optimistically, losing the user's correction;
  // now the draft survives an error and the danger Alert stays visible so they
  // can retry.
  const runSubmit = useCallback(
    async (intent: TSpeakerEditIntent): Promise<void> => {
      try {
        await onSubmit(intent)
        setNewName("")
        setMergeTarget("")
        setPickerOpen(false)
        // In modal use the card is always `defaultOpen`; collapsing it would hide
        // the body while the modal stays open (the smart wrapper closes the modal
        // on success), so only collapse the inline (non-modal) card.
        if (!defaultOpen) setExpanded(false)
      } catch {
        // Smart wrapper surfaces the error via the danger Alert; keep the
        // draft + expanded state so the user can fix and retry.
      }
    },
    [onSubmit, defaultOpen],
  )

  const handleSaveDisplayName = useCallback((): void => {
    const name = displayNameDraft.trim()
    if (!name || name === speakerDisplayName) return
    void runSubmit({ kind: "rename", name })
  }, [displayNameDraft, speakerDisplayName, runSubmit])

  const handleSave = useCallback((): void => {
    // A picked merge target — confirm, then merge into its entity (or, on a legacy
    // meeting with no entity id, into its name via the smart wrapper's shim).
    if (mergeTarget) {
      const option = mergeOptions.find((o) => o.displayName === mergeTarget)
      if (!option) return
      modals.openConfirmModal({
        title: i18n._(msg`Merge speakers`),
        children: (
          <Text size="sm">
            <Trans>
              Merge {speakerDisplayName} into {option.displayName}? Their transcript lines and voice samples combine,
              and it can&rsquo;t be undone.
            </Trans>
          </Text>
        ),
        labels: { confirm: i18n._(msg`Merge`), cancel: i18n._(msg`Cancel`) },
        confirmProps: { color: "danger" },
        onConfirm: () => {
          void runSubmit({ kind: "merge", targetEntityId: option.entityId, targetName: option.displayName })
        },
      })
      return
    }
    const name = newName.trim()
    if (!name || name === speakerDisplayName) return
    void runSubmit({ kind: "rename", name })
  }, [mergeTarget, mergeOptions, newName, speakerDisplayName, runSubmit, i18n])

  const handleRosterCommit = useCallback(
    (choice: TSpeakerRosterChoice): void => {
      if (choice.kind === "person") {
        void runSubmit({ kind: "link", speakerId: choice.person.id, name: choice.person.name })
        return
      }
      const name = choice.name.trim()
      if (name.length === 0 || name === speakerDisplayName) return
      void runSubmit({ kind: "rename", name })
    },
    [runSubmit, speakerDisplayName],
  )

  const handleSaveProfile = useCallback((): void => {
    onSaveProfile({
      title: editTitle || null,
      color: editColor || null,
    })
  }, [editTitle, editColor, onSaveProfile])

  // Apple/WebKit can't decode the raw WebM/Opus recording, so play the AAC/MP4
  // variant there (same `?format=m4a` path the main Player uses). Without this
  // the voice-sample buttons silently fail on iOS Safari.
  const samplePlaybackUrl = useMemo(() => (audioUrl ? resolvePlaybackSrc(audioUrl) : audioUrl), [audioUrl])

  const playSample = useCallback(
    (index: number) => {
      const seg = sampleSegments[index]
      if (!seg || !audioUrl) return

      if (playingIndex === index) {
        const a = audioRef.current
        if (a) {
          a.pause()
          a.ontimeupdate = null
          a.onended = null
        }
        setPlayingIndex(null)
        return
      }

      // Reuse a single Audio element across plays. Creating a fresh `new
      // Audio(audioUrl)` per click triggered a new fetch + decode cycle
      // every time and (more visibly) seeks BEFORE the file had buffered
      // enough to honour the requested currentTime — the browser silently
      // clamps to whatever's already buffered (often 0), so all three
      // sample buttons would play the start of the recording. Holding one
      // Audio across plays lets the browser keep its buffer + speeds up
      // every subsequent seek.
      let audio = audioRef.current
      if (!audio || (samplePlaybackUrl && !audio.src.endsWith(samplePlaybackUrl))) {
        audio = new Audio(samplePlaybackUrl ?? undefined)
        audio.preload = "auto"
        audioRef.current = audio
      } else {
        audio.pause()
        audio.ontimeupdate = null
        audio.onended = null
      }

      const captured = audio

      captured.ontimeupdate = (): void => {
        if (captured.currentTime >= seg.end) {
          captured.pause()
          captured.ontimeupdate = null
          setPlayingIndex(null)
        }
      }
      captured.onended = (): void => setPlayingIndex(null)

      const seekAndPlay = (): void => {
        captured.currentTime = seg.start
        setPlayingIndex(index)
        captured.play().catch(() => setPlayingIndex(null))
      }

      if (captured.readyState >= 1 /* HAVE_METADATA */) {
        seekAndPlay()
      } else {
        captured.addEventListener(
          "loadedmetadata",
          () => {
            seekAndPlay()
          },
          { once: true },
        )
        try {
          captured.load()
        } catch {
          // Older browsers throw on load() during a previous play —
          // ignore; seekAndPlay() will retry.
        }
      }
    },
    [sampleSegments, audioUrl, samplePlaybackUrl, playingIndex],
  )

  // Stop playback when card unmounts.
  useEffect(() => {
    return (): void => {
      const a = audioRef.current
      if (a) {
        a.pause()
        a.ontimeupdate = null
        a.onended = null
      }
    }
  }, [])

  const displayQuotes = quotes.length > 0 ? quotes : fallbackLines

  // Presence badge — only meaningful (and only shown) on a hybrid meeting, where
  // the parent flags `showPresence`. "unknown" carries no signal, so no badge.
  const presenceBadge =
    showPresence && (presence === "remote" || presence === "in_room") ? (
      <Badge size="xs" variant="light" color={presence === "remote" ? "info" : "gray"}>
        {presence === "remote" ? <Trans>Remote</Trans> : <Trans>In the room</Trans>}
      </Badge>
    ) : null

  return (
    <Card
      withBorder
      padding={0}
      style={isUnknown ? { borderColor: "var(--mantine-color-danger-light-color)" } : undefined}
    >
      {!defaultOpen && (
        <UnstyledButton
          onClick={handleExpand}
          w="100%"
          p="xs"
          aria-label={
            expanded
              ? i18n._(msg`Collapse speaker ${speakerDisplayName}`)
              : i18n._(msg`Expand speaker ${speakerDisplayName}`)
          }
          aria-expanded={expanded}
        >
          <Group gap="sm" wrap="nowrap">
            <Avatar size="sm" color={isUnknown ? "warning" : "info"} radius="xl">
              {speakerInitials(speakerDisplayName)}
            </Avatar>
            <Text size="sm" fw={600}>
              {speakerDisplayName}
            </Text>
            {isUnknown && (
              <Badge size="xs" color="warning" variant="light">
                <Trans>needs ID</Trans>
              </Badge>
            )}
            {presenceBadge}
            <Text size="xs" c="dimmed" ml="auto">
              {expanded ? "▾" : "▸"}
            </Text>
          </Group>
        </UnstyledButton>
      )}

      <Collapse in={expanded}>
        <Stack gap="xs" p="xs" pt={defaultOpen ? "xs" : 0}>
          {/* In modal use the collapsed header (which carries the badge) is hidden,
              so surface presence at the top of the body instead. */}
          {defaultOpen && presenceBadge ? <Group gap="xs">{presenceBadge}</Group> : null}
          {/* Quotes / lines preview */}
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              {quotes.length > 0 ? i18n._(msg`Representative quotes:`) : i18n._(msg`Recent lines:`)}
            </Text>
            {loadingLines ? (
              <Stack gap={4}>
                <Skeleton height={12} width="90%" />
                <Skeleton height={12} width="70%" />
              </Stack>
            ) : displayQuotes.length === 0 ? (
              <Text size="xs" c="dimmed" fs="italic">
                <Trans>No transcript lines yet</Trans>
              </Text>
            ) : (
              displayQuotes.map((line) => (
                <Text key={line} size="xs" c="dimmed" fs="italic">
                  &ldquo;{line}&rdquo;
                </Text>
              ))
            )}
          </div>

          {/* Voice samples */}
          {sampleSegments.length > 0 && audioUrl && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>
                <Trans>Voice samples:</Trans>
              </Text>
              <Stack gap={4}>
                {sampleSegments.map((seg, i) => (
                  <Group key={seg.start} gap="xs" wrap="nowrap">
                    <Button
                      size="compact-xs"
                      variant={playingIndex === i ? "filled" : "light"}
                      color={playingIndex === i ? "danger" : "info"}
                      onClick={() => playSample(i)}
                      leftSection={playingIndex === i ? <IconPlayerStop size={11} /> : <IconPlayerPlay size={11} />}
                      style={{ flexShrink: 0 }}
                    >
                      {playingIndex === i ? <Trans>Stop</Trans> : <Trans>Play</Trans>}
                    </Button>
                    <Text size="xs" c="dimmed" fs="italic" lineClamp={1}>
                      &ldquo;{seg.text}&rdquo;
                    </Text>
                  </Group>
                ))}
              </Stack>
            </div>
          )}

          {!isUnknown && !mergeTarget && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>
                <Trans>Display name</Trans>
              </Text>
              <Group gap="xs" align="flex-end" wrap="nowrap">
                <TextInput
                  size="xs"
                  style={{ flex: 1 }}
                  value={displayNameDraft}
                  onChange={(e) => setDisplayNameDraft(e.currentTarget.value)}
                  aria-label={i18n._(msg`Display name`)}
                  data-testid="speaker-display-name"
                />
                <Button
                  size="compact-xs"
                  onClick={handleSaveDisplayName}
                  loading={saving}
                  disabled={!displayNameDraft.trim() || displayNameDraft.trim() === speakerDisplayName}
                >
                  <Trans>Save name</Trans>
                </Button>
              </Group>
            </div>
          )}

          {knownVoiceId ? (
            <Anchor
              size="xs"
              renderRoot={(props) => <Link to="/speakers" search={{ voice: knownVoiceId }} {...props} />}
            >
              <Trans>Open in Known voices</Trans>
            </Anchor>
          ) : null}

          {/* Profile: title + color */}
          <div>
            <Text size="xs" c="dimmed" mb={6}>
              <Trans>Profile</Trans>
            </Text>
            <Stack gap={6}>
              <TextInput
                size="xs"
                placeholder={i18n._(msg`Title or role (e.g. Engineering Lead)`)}
                value={editTitle}
                onChange={(e) => setEditTitle(e.currentTarget.value)}
              />
              <ColorInput
                size="xs"
                placeholder={i18n._(msg`Color (leave blank for auto)`)}
                value={editColor}
                onChange={setEditColor}
                swatches={[...SPEAKER_COLORS]}
                swatchesPerRow={12}
                format="hex"
              />
              <Button
                size="compact-xs"
                variant="light"
                onClick={handleSaveProfile}
                loading={savingProfile}
                disabled={!editTitle && !editColor}
              >
                <Trans>Save profile</Trans>
              </Button>
            </Stack>
          </div>

          {/* AI name suggestion — an info banner above the rename input. Only
              set for still-unidentified speakers the recognition pipeline has a
              proposal for; confirming applies the rename + enrolls the voice. */}
          {suggestion && (
            <Alert color="info" variant="light" p="xs" data-testid="speaker-suggestion-banner">
              <Stack gap={6}>
                <Text size="xs">
                  {suggestionEvidence ? (
                    <Trans>
                      AI thinks this is <strong>{suggestion.name}</strong> ({suggestionEvidence})
                    </Trans>
                  ) : (
                    <Trans>
                      AI thinks this is <strong>{suggestion.name}</strong>
                    </Trans>
                  )}
                </Text>
                <Group gap="xs">
                  <Button size="compact-xs" variant="light" color="info" onClick={handleUseSuggestion}>
                    <Trans>Use this name</Trans>
                  </Button>
                  <Button
                    size="compact-xs"
                    color="success"
                    loading={suggestionBusy}
                    onClick={onConfirmSuggestion}
                    data-testid="speaker-suggestion-confirm"
                  >
                    <Trans>Confirm</Trans>
                  </Button>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="danger"
                    disabled={suggestionBusy}
                    onClick={onRejectSuggestion}
                    data-testid="speaker-suggestion-dismiss"
                  >
                    <Trans>Dismiss</Trans>
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {/* Merge with existing speaker — visually distinct (warning colour +
              merge icon) from the neutral rename input so the two destructive
              and non-destructive actions aren't mistaken for each other. */}
          {mergeOptions.length > 0 && (
            <div>
              <Group gap={6} mb={4} wrap="nowrap">
                <IconArrowMerge size={12} style={{ opacity: 0.7 }} />
                <Text size="xs" c="dimmed">
                  <Trans>Merge into another speaker (combines segments):</Trans>
                </Text>
              </Group>
              <Group gap={4}>
                {mergeOptions.map((opt) => (
                  <Button
                    key={opt.displayName}
                    size="compact-xs"
                    color="warning"
                    variant={mergeTarget === opt.displayName ? "filled" : "light"}
                    leftSection={<IconArrowMerge size={11} />}
                    onClick={() => {
                      setMergeTarget(mergeTarget === opt.displayName ? "" : opt.displayName)
                      setNewName("")
                    }}
                    data-testid={`speaker-merge-target-${opt.displayName}`}
                  >
                    {opt.displayName}
                    {opt.farEnd && (
                      <Text span size="xs" c="dimmed" ml={4}>
                        <Trans>(far end)</Trans>
                      </Text>
                    )}
                  </Button>
                ))}
              </Group>
            </div>
          )}

          {!mergeTarget && (pickerOpen || isUnknown) && (
            <SpeakerRosterPicker
              query={newName}
              onQueryChange={(q) => {
                setNewName(q)
                setPickerOpen(true)
                onRosterQueryChange?.(q)
              }}
              people={rosterPeople}
              loading={rosterLoading}
              onCommit={handleRosterCommit}
              onCancel={() => {
                setPickerOpen(false)
                setNewName("")
              }}
              using={saving}
              error={error.length > 0 ? error : null}
            />
          )}

          {!mergeTarget && !pickerOpen && !isUnknown && (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => setPickerOpen(true)}
              data-testid="speaker-rename-input"
            >
              <Trans>Pick someone else</Trans>
            </Button>
          )}

          {mergeTarget && (
            <Group gap="xs">
              <Button
                size="compact-xs"
                color="warning"
                leftSection={<IconArrowMerge size={11} />}
                onClick={handleSave}
                loading={saving}
                data-testid="speaker-rename-save"
              >
                {i18n._(msg`Merge into ${mergeTarget}`)}
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => {
                  setMergeTarget("")
                  setNewName("")
                }}
              >
                <Trans>Cancel</Trans>
              </Button>
            </Group>
          )}

          {error && (
            <Alert color="danger" variant="light" p="xs">
              <Text size="xs">{error}</Text>
            </Alert>
          )}

          <Group justify="flex-end" mt="xs">
            <Button
              size="compact-xs"
              variant="subtle"
              color="danger"
              leftSection={<IconTrash size={12} />}
              loading={discarding}
              onClick={() =>
                modals.openConfirmModal({
                  title: i18n._(msg`Discard ${speakerDisplayName}?`),
                  children: (
                    <Text size="sm">
                      <Trans>
                        {speakerDisplayName}'s name is removed and their transcript lines are reassigned to "Unknown".
                        The lines are kept — you can re-identify the speaker later — and the summary attribution updates
                        to match.
                      </Trans>
                    </Text>
                  ),
                  labels: { confirm: i18n._(msg`Discard`), cancel: i18n._(msg`Cancel`) },
                  confirmProps: { color: "danger" },
                  onConfirm: onDiscard,
                })
              }
            >
              <Trans>Discard speaker</Trans>
            </Button>
          </Group>
        </Stack>
      </Collapse>
    </Card>
  )
}
